import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const HIDE_KEY = 'nesolearn-hide-stats';

// One stat tile, laid out like Kaggle's:
//   (icon circle)  Title
//   [ bracket      number
//                  small caption
//
// A tile can have:
//   to:      route to open when clicked
//   onClick: function to run when clicked (used for scrolling to a section)
//   soon:    true for features that are not built yet. Clicking briefly shows
//            "coming soon" in the caption instead of navigating.
// `icon` can be an Icon name (string) or any React node, so existing custom
// icons can be dropped in without changing them.
const Tile = ({ tile }) => {
  const [hint, setHint] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const showHint = () => {
    setHint(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHint(false), 2000);
  };

  const iconNode = tile.lottie ? (
    <DotLottieReact src={tile.lottie} loop autoplay style={{ width: '100%', height: '100%' }} />
  ) : typeof tile.icon === 'string' ? (
    <Icon name={tile.icon} size={18} className="text-foreground" />
  ) : (
    tile.icon
  );

  const body = (
    <>
      <div className="flex items-center gap-3">
        <span className={`w-10 h-10 flex-shrink-0 rounded-full ${tile.tint || 'bg-muted'} flex items-center justify-center`}>
          {iconNode}
        </span>
        <span className="text-base font-medium text-foreground truncate">{tile.label}</span>
      </div>

      <div className="mt-3 flex gap-3">
        <span className="w-10 flex-shrink-0 flex justify-center" aria-hidden="true">
          <span className="block w-2 h-14 border-l border-t border-b border-border" />
        </span>
        <div className="min-w-0">
          <p className="text-2xl leading-none text-foreground">{tile.value}</p>
          <p className={`mt-1.5 text-sm ${hint ? 'text-accent font-medium' : 'text-muted-foreground'}`}>
            {hint ? 'coming soon' : tile.caption}
          </p>
        </div>
      </div>
    </>
  );

  const shared =
    'block w-full text-left rounded-xl p-2 -m-2 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

  if (tile.to && !tile.soon) {
    return (
      <Link to={tile.to} className={shared} aria-label={`${tile.label}: ${tile.value} ${tile.caption}`}>
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={tile.soon ? showHint : tile.onClick}
      className={shared}
      aria-label={`${tile.label}: ${tile.value} ${tile.caption}`}
    >
      {body}
    </button>
  );
};

const StatsStrip = ({ tiles = [] }) => {
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleHidden = () => {
    setHidden((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(HIDE_KEY, next ? '1' : '0');
      } catch {}
      return next;
    });
  };

  return (
    <section aria-label="Your stats" style={{ marginTop: hidden ? 4 : 40 }}>
      {!hidden && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-10 px-2">
          {tiles.map((tile) => (
            <Tile key={tile.label} tile={tile} />
          ))}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={toggleHidden}
          className="text-sm font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
        >
          {hidden ? 'Show stats' : 'Hide stats'}
        </button>
      </div>
      <div className="mt-3 border-t border-border" />
    </section>
  );
};

export default StatsStrip;